<template>
  <div class="app-root">
    <!-- Navbar -->
    <nav class="g3-navbar" v-if="showNavbar">
      <div class="g3-navbar-inner">
        <!-- Logo -->
        <router-link class="g3-navbar-brand" to="/">
          <img src="/logo_blanco.png" alt="Gestion360" class="g3-navbar-logo">
        </router-link>

        <!-- Toggler mobile -->
        <button class="g3-navbar-toggler" @click="menuOpen = !menuOpen">
          <i :class="menuOpen ? 'bi bi-x-lg' : 'bi bi-list'"></i>
        </button>

        <!-- Menu -->
        <div class="g3-navbar-menu" :class="{ 'g3-navbar-open': menuOpen }">
          <!-- Menu dinamico -->
          <DynamicMenu />

          <!-- Empresa badge -->
          <div class="g3-navbar-empresa" v-if="selectedEmpresa">
            <i class="bi bi-buildings"></i>
            <span>{{ selectedEmpresa.Codigo || selectedEmpresa.Nombre }}</span>
          </div>

          <!-- Pendientes del usuario -->
          <router-link
            v-if="isAuthenticated"
            to="/pendientes"
            class="g3-navbar-pend"
            :title="pend.total > 0 ? 'Tienes ' + pend.total + ' pendiente(s)' : 'Sin pendientes'"
          >
            <i class="bi bi-bell"></i>
            <span v-if="pend.total > 0" class="g3-pend-badge g3-pend-badge-top">{{ pend.total }}</span>
          </router-link>

          <!-- User menu -->
          <div class="g3-navbar-user" v-if="isAuthenticated">
            <button class="g3-user-btn" @click="userMenuOpen = !userMenuOpen" @blur="closeUserMenu">
              <span class="g3-user-avatar">{{ userInitial }}</span>
              <span class="g3-user-name">{{ userName }}</span>
              <i class="bi bi-chevron-down g3-user-chevron" :class="{ 'g3-rotated': userMenuOpen }"></i>
            </button>
            <transition name="g3-dropdown">
              <div class="g3-user-dropdown" v-if="userMenuOpen">
                <div class="g3-dropdown-header">
                  <span class="g3-dropdown-uid">{{ userId }}</span>
                </div>
                <div class="g3-dropdown-divider"></div>
                <template v-if="asistenciaEstado && asistenciaEstado.EmpleadoVinculado">
                  <div class="g3-dropdown-asistencia">
                    <div class="g3-asistencia-info">
                      <i class="bi bi-clock-history"></i>
                      <div>
                        <span class="g3-asistencia-label">{{ asistenciaEstado.SiguienteAccion }}</span>
                        <span v-if="asistenciaEstado.UltimaChecada" class="g3-asistencia-ultima">
                          Última: {{ formatHoraChecada(asistenciaEstado.UltimaChecada) }}
                        </span>
                      </div>
                    </div>
                    <button
                      class="g3-asistencia-btn"
                      :class="asistenciaEstado.SiguienteEventoClave === 'SALIDA' ? 'g3-asistencia-btn-salida' : 'g3-asistencia-btn-entrada'"
                      :disabled="asistenciaRegistrando"
                      @click="registrarChecada"
                    >
                      <i class="bi" :class="asistenciaEstado.SiguienteEventoClave === 'SALIDA' ? 'bi-box-arrow-right' : 'bi-box-arrow-in-right'"></i>
                      {{ asistenciaRegistrando ? 'Registrando...' : asistenciaEstado.SiguienteAccion }}
                    </button>
                  </div>
                  <div class="g3-dropdown-divider"></div>
                </template>
                <router-link class="g3-dropdown-item" to="/perfil" @click="userMenuOpen = false">
                  <i class="bi bi-person-badge"></i>Mi Perfil
                </router-link>
                <div class="g3-dropdown-divider"></div>
                <template v-if="selectedEmpresa">
                  <div class="g3-dropdown-empresa">
                    <i class="bi bi-buildings"></i>
                    <div>
                      <span class="g3-empresa-name">{{ selectedEmpresa.Nombre }}</span>
                      <span class="g3-empresa-code">{{ selectedEmpresa.Codigo || 'ID: ' + selectedEmpresa.EmpresaId }}</span>
                    </div>
                  </div>
                  <a v-if="hasMultipleEmpresas" class="g3-dropdown-item" href="#" @click.prevent="showEmpresaSelector">
                    <i class="bi bi-arrow-repeat"></i>Cambiar empresa
                  </a>
                  <div class="g3-dropdown-divider"></div>
                  <!-- Selector de tema -->
                  <div class="g3-dropdown-themes">
                    <span class="g3-dropdown-themes-label">Tema</span>
                    <div class="g3-theme-pills">
                      <button
                        v-for="t in THEMES"
                        :key="t.id"
                        class="g3-theme-pill"
                        :class="{ 'g3-theme-pill-active': currentTheme === t.id }"
                        :title="t.label"
                        @click="cambiarTema(t.id)"
                      >{{ t.icon }}</button>
                    </div>
                  </div>
                  <div class="g3-dropdown-divider"></div>
                </template>
                <a class="g3-dropdown-item g3-dropdown-logout" href="#" @click.prevent="handleLogout">
                  <i class="bi bi-box-arrow-right"></i>Cerrar sesion
                </a>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </nav>

    <!-- Subbar -->
    <div class="g3-subbar" v-if="showNavbar && pageTitle">
      <div class="g3-subbar-inner">
        <router-link to="/" class="g3-subbar-home"><i class="bi bi-house-door"></i></router-link>
        <i class="bi bi-chevron-right g3-subbar-sep"></i>
        <span class="g3-subbar-title">{{ pageTitle }}</span>
      </div>
    </div>

    <!-- Content -->
    <main class="g3-main" :class="{ 'g3-main-with-nav': showNavbar, 'g3-main-with-subbar': showNavbar && pageTitle }">
      <EmpresaSelector
        v-if="showSelector"
        @empresa-selected="onEmpresaSelected"
        @close="showSelector = false"
      />
      <router-view />
    </main>

    <!-- Footer -->
    <footer class="g3-footer" v-if="showNavbar">
      <span>&copy; {{ new Date().getFullYear() }} <a href="https://gestion360.mx" target="_blank" rel="noopener" class="g3-footer-link">Gestion360</a> &mdash; <a href="https://tangentemexico.com" target="_blank" rel="noopener" class="g3-footer-link">Sistemas Tangente del Norte</a></span>
      <span class="g3-footer-build">build {{ buildTime }}</span>
    </footer>

    <!-- Toast global -->
    <AppToast />

    <!-- Modal cierre de sesión -->
    <transition name="g3-logout-fade">
      <div v-if="loggingOut" class="g3-logout-overlay">
        <div class="g3-logout-card">
          <div class="g3-logout-spinner"></div>
          <p class="g3-logout-msg">Cerrando sesión...</p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import authService from '@/services/auth';
import DynamicMenu from '@/shared/components/DynamicMenu.vue';
import EmpresaSelector from '@/shared/components/EmpresaSelector.vue';
import AppToast from '@/shared/components/AppToast.vue';
import { useTheme, THEMES } from '@/composables/useTheme';
import asistenciaService from '@/modules/asistencia/asistencia.service';
import { pendientesState, asegurarRefrescoPendientes, detenerRefrescoPendientes } from '@/shared/services/pendientes.service';

export default {
  name: 'App',
  components: {
    DynamicMenu,
    EmpresaSelector,
    AppToast
  },
  setup() {
    const { currentTheme, cambiarTema } = useTheme();
    return { currentTheme, cambiarTema, THEMES };
  },
  data() {
    return {
      isAuthenticated: false,
      currentUser: null,
      selectedEmpresa: null,
      showSelector: false,
      menuOpen: false,
      userMenuOpen: false,
      loggingOut: false,
      asistenciaEstado: null,
      asistenciaRegistrando: false,
      pend: pendientesState,
      buildTime: process.env.VUE_APP_BUILD_TIME || ''
    };
  },
  computed: {
    hasMultipleEmpresas() {
      if (!this.currentUser?.Usuario?._UsuarioMo?._EmpresaList) return false;
      return this.currentUser.Usuario._UsuarioMo._EmpresaList.filter(e => e.EsActivo).length > 1;
    },
    showNavbar() {
      return this.$route.path !== '/login';
    },
    userName() {
      return this.currentUser?.Usuario?._UsuarioMo?.Nombre || 'Usuario';
    },
    userId() {
      return this.currentUser?.Usuario?._UsuarioMo?.UsuarioId || '';
    },
    userInitial() {
      return this.userName.charAt(0).toUpperCase();
    },
    pageTitle() {
      const name = this.$route.name;
      if (!name || name === 'dashboard' || name === 'login') return '';
      return this.$route.meta?.title || this.$route.name?.replace(/-/g, ' ') || '';
    }
  },
  watch: {
    userMenuOpen(abierto) {
      if (abierto) this.cargarMiAsistencia();
    }
  },
  created() {
    this.checkAuth();
    this.$router.beforeEach((to, from, next) => {
      this.checkAuth();
      if (to.meta.requiresAuth && !this.isAuthenticated) {
        next('/login');
      } else {
        this.menuOpen = false;
        next();
      }
    });
    this.$router.afterEach((to) => {
      if (to.path !== '/login') asegurarRefrescoPendientes();
    });
  },
  unmounted() {
    detenerRefrescoPendientes();
  },
  methods: {
    checkAuth() {
      this.isAuthenticated = authService.isAuthenticated();
      if (this.isAuthenticated) {
        this.currentUser = authService.getUser();
        this.selectedEmpresa = authService.getSelectedEmpresa();
      } else {
        this.selectedEmpresa = null;
      }
    },
    showEmpresaSelector() {
      this.userMenuOpen = false;
      this.showSelector = true;
    },
    onEmpresaSelected(empresa) {
      this.selectedEmpresa = empresa;
      this.showSelector = false;
      window.location.reload();
    },
    closeUserMenu() {
      setTimeout(() => { this.userMenuOpen = false; }, 200);
    },
    async cargarMiAsistencia() {
      try {
        const r = await asistenciaService.miEstado();
        this.asistenciaEstado = r.Number >= 0 ? r.OData : null;
      } catch (e) {
        console.error('Error al consultar mi asistencia:', e);
        this.asistenciaEstado = null;
      }
    },
    formatHoraChecada(fecha) {
      if (!fecha) return '';
      return new Date(fecha).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    },
    async registrarChecada() {
      this.asistenciaRegistrando = true;
      try {
        const r = await asistenciaService.miRegistrar();
        if (r.Number >= 0) {
          this.asistenciaEstado = r.OData;
          this.$toast.success(r.Message);
        } else {
          this.$toast.error(r.Message);
        }
      } catch (e) {
        console.error('Error al registrar checada:', e);
      } finally {
        this.asistenciaRegistrando = false;
      }
    },
    async handleLogout() {
      this.userMenuOpen = false;
      this.loggingOut = true;
      try {
        await authService.logout();
      } catch (e) {
        console.error('Error al cerrar sesion:', e);
      }
      this.isAuthenticated = false;
      this.currentUser = null;
      this.selectedEmpresa = null;
      this.asistenciaEstado = null;
      detenerRefrescoPendientes();
      this.loggingOut = false;
      this.$router.push('/login');
    }
  }
}
</script>

<style>
/* ========== Global Reset ========== */
*, *::before, *::after {
  box-sizing: border-box;
}

/* ========== Navbar ========== */
.g3-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: linear-gradient(135deg, var(--t-navbar-from, #0a3d6b), var(--t-navbar-to, #1272c4));
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  overflow: visible;
}

.g3-navbar-inner {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  height: 100%;
  padding: 0 1.25rem;
}

.g3-navbar-brand {
  display: flex;
  align-items: center;
  text-decoration: none;
  margin-right: 1.5rem;
  flex-shrink: 0;
}

.g3-navbar-logo {
  height: 28px;
}

.g3-navbar-toggler {
  display: none;
  background: none;
  border: none;
  color: #ffffff;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  margin-left: auto;
}

.g3-navbar-menu {
  display: flex;
  align-items: center;
  flex: 1;
  height: 100%;
  flex-wrap: nowrap;
  min-width: 0;
}

/* ========== Empresa badge ========== */
.g3-navbar-empresa {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: auto;
  margin-right: 0.75rem;
  padding: 0.3rem 0.7rem;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.g3-navbar-empresa i {
  font-size: 0.75rem;
  opacity: 0.7;
}

/* ========== User menu ========== */
.g3-navbar-user {
  margin-left: auto;
  position: relative;
  flex-shrink: 0;
}

.g3-user-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 0.35rem 0.75rem;
  color: #ffffff;
  cursor: pointer;
  transition: background 0.2s;
  font-family: inherit;
  font-size: 0.85rem;
}

.g3-user-btn:hover {
  background: rgba(255, 255, 255, 0.18);
}

.g3-user-avatar {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;
}

.g3-user-name {
  font-weight: 500;
}

.g3-user-chevron {
  font-size: 0.65rem;
  transition: transform 0.2s;
}

.g3-rotated {
  transform: rotate(180deg);
}

/* ========== Dropdown (override Bootstrap) ========== */
.g3-navbar-user .g3-user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  left: auto;
  background: #ffffff;
  border: none;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 240px;
  max-width: 280px;
  overflow: hidden;
  z-index: 1001;
  padding: 0;
  margin: 0;
  font-family: 'Outfit', 'Poppins', -apple-system, sans-serif;
  font-size: 0.85rem;
}

.g3-user-dropdown .g3-dropdown-header {
  padding: 0.6rem 1rem;
  margin: 0;
}

.g3-user-dropdown .g3-dropdown-uid {
  font-size: 0.78rem;
  color: #a0aec0;
  line-height: 1.3;
}

.g3-user-dropdown .g3-dropdown-divider {
  height: 1px;
  background: #edf2f7;
  margin: 0;
  border: none;
}

.g3-user-dropdown .g3-dropdown-empresa {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 1rem;
  margin: 0;
}

.g3-user-dropdown .g3-dropdown-empresa > i {
  color: #1272c4;
  font-size: 1rem;
  flex-shrink: 0;
}

.g3-user-dropdown .g3-empresa-name {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.3;
}

.g3-user-dropdown .g3-empresa-code {
  display: block;
  font-size: 0.7rem;
  color: #a0aec0;
  line-height: 1.3;
}

.g3-user-dropdown .g3-dropdown-item {
  display: flex !important;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem !important;
  color: #4a5568 !important;
  text-decoration: none !important;
  font-size: 0.82rem !important;
  font-weight: 400;
  transition: background 0.15s;
  background: transparent;
  border: none;
  border-radius: 0;
  white-space: nowrap;
  line-height: 1.4;
}

.g3-user-dropdown .g3-dropdown-item:hover {
  background: #f7fafc !important;
  color: #1272c4 !important;
}

.g3-user-dropdown .g3-dropdown-item i {
  font-size: 0.85rem;
  width: 18px;
  text-align: center;
  flex-shrink: 0;
}

.g3-user-dropdown .g3-dropdown-logout:hover {
  color: #e53e3e !important;
}

.g3-dropdown-asistencia {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.6rem 1rem;
}

.g3-asistencia-info {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
}

.g3-asistencia-info > i {
  color: #1272c4;
  font-size: 1rem;
  flex-shrink: 0;
}

.g3-asistencia-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3748;
  line-height: 1.3;
}

.g3-asistencia-ultima {
  display: block;
  font-size: 0.7rem;
  color: #a0aec0;
  line-height: 1.3;
}

.g3-asistencia-btn {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.7rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity 0.15s;
}

.g3-asistencia-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.g3-asistencia-btn-entrada {
  background: #2f9e44;
}

.g3-asistencia-btn-salida {
  background: #e8590c;
}

.g3-dropdown-themes {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
}

.g3-dropdown-themes-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.g3-theme-pills {
  display: flex;
  gap: 0.3rem;
}

.g3-theme-pill {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid transparent;
  background: #f7fafc;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  padding: 0;
}

.g3-theme-pill:hover {
  border-color: #cbd5e0;
  background: #edf2f7;
}

.g3-theme-pill-active {
  border-color: #1272c4;
  background: #ebf8ff;
}

.g3-dropdown-enter-active {
  animation: g3-drop 0.15s ease-out;
}

.g3-dropdown-leave-active {
  animation: g3-drop 0.1s ease-in reverse;
}

@keyframes g3-drop {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ========== Subbar ========== */
.g3-subbar {
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  height: 30px;
  background: #ffffff;
  border-bottom: 1px solid #edf2f7;
  z-index: 999;
}

.g3-subbar-inner {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  height: 100%;
  padding: 0 1rem;
  max-width: 1400px;
  margin: 0 auto;
  font-size: 0.76rem;
}

.g3-subbar-home {
  color: #718096;
  text-decoration: none;
  transition: color 0.15s;
}

.g3-subbar-home:hover {
  color: #1272c4;
}

.g3-subbar-sep {
  font-size: 0.48rem;
  color: #cbd5e0;
}

.g3-subbar-title {
  color: #2d3748;
  font-weight: 600;
  line-height: 1;
}

/* ========== Main ========== */
.g3-main {
  min-height: calc(100vh - 48px);
  background: #f8fafc;
}

.g3-main-with-nav {
  padding-top: 56px;
}

.g3-main-with-subbar {
  padding-top: 86px;
}

/* ========== Footer ========== */
.g3-footer {
  background: var(--t-footer-bg, #1a202c);
  color: var(--t-footer-color, #718096);
  text-align: center;
  padding: 1rem;
  font-size: 0.75rem;
}

.g3-footer-link {
  color: var(--t-footer-link, #90cdf4);
  text-decoration: none;
  transition: color 0.15s;
}

.g3-footer-link:hover {
  color: #ffffff;
  text-decoration: underline;
}

.g3-footer-build {
  margin-left: 1rem;
  opacity: 0.45;
  font-size: 0.7rem;
  font-family: monospace;
}

/* ========== Pendientes (globitos rojos) ========== */
.g3-pend-badge {
  display: inline-block;
  min-width: 1.15rem;
  margin-left: 0.35rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: #dc3545;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.15rem;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
}

.g3-navbar-pend {
  position: relative;
  display: flex;
  align-items: center;
  margin-right: 0.75rem;
  padding: 0.3rem 0.45rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.15rem;
  text-decoration: none;
  flex-shrink: 0;
}

.g3-navbar-pend:hover {
  color: #fff;
}

.g3-pend-badge-top {
  position: absolute;
  top: -2px;
  right: -6px;
  margin-left: 0;
}

/* ========== DynamicMenu navbar styles ========== */
.g3-navbar .navbar-nav {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  list-style: none;
  margin: 0 !important;
  padding: 0 !important;
  gap: 0.25rem;
  flex-wrap: nowrap !important;
  flex-shrink: 1;
  min-width: 0;
}

.g3-navbar .nav-item {
  position: relative;
}

.g3-navbar .nav-link {
  display: flex !important;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.75rem !important;
  color: rgba(255, 255, 255, 0.8) !important;
  text-decoration: none !important;
  font-size: 0.85rem !important;
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.15s;
  white-space: nowrap;
}

.g3-navbar .nav-link:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

.g3-navbar .router-link-exact-active:not(.g3-dropdown-item) {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.15) !important;
}

.g3-navbar .dropdown-toggle::after {
  display: inline-block;
  margin-left: 0.3rem;
  vertical-align: 0.15em;
  content: "";
  border-top: 0.28em solid;
  border-right: 0.28em solid transparent;
  border-left: 0.28em solid transparent;
}

.g3-navbar .dropdown-menu {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  background: #ffffff !important;
  border: none !important;
  border-radius: 8px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  padding: 0.35rem 0 !important;
  min-width: 200px !important;
  max-height: none !important;
  z-index: 1002;
  font-size: 0.85rem;
}

.g3-navbar .dropdown-menu .dropdown-item {
  display: flex !important;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 1rem !important;
  color: #4a5568 !important;
  font-size: 0.82rem !important;
  text-decoration: none !important;
  transition: all 0.15s;
  background: transparent !important;
}

.g3-navbar .dropdown-menu .dropdown-item:hover,
.g3-navbar .dropdown-menu .dropdown-item:focus {
  background: #f7fafc !important;
  color: #1272c4 !important;
}

/* ========== Responsive ========== */
@media (max-width: 900px) {
  .g3-navbar-toggler {
    display: block;
  }

  .g3-navbar-menu {
    display: none;
    position: absolute;
    top: 56px;
    left: 0;
    right: 0;
    background: #0a3d6b;
    flex-direction: column;
    align-items: stretch;
    padding: 0.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1001;
  }

  .g3-navbar-menu.g3-navbar-open {
    display: flex;
  }

  .g3-navbar .navbar-nav {
    flex-direction: column !important;
    width: 100%;
  }

  .g3-navbar-user {
    margin-left: 0;
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .g3-user-dropdown {
    position: static;
    box-shadow: none;
    border-radius: 8px;
    margin-top: 0.5rem;
  }
}

/* ========== Logout overlay ========== */
.g3-logout-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 61, 107, 0.55);
  backdrop-filter: blur(3px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.g3-logout-card {
  background: #ffffff;
  border-radius: 14px;
  padding: 2.5rem 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
  min-width: 200px;
}

.g3-logout-spinner {
  width: 44px;
  height: 44px;
  border: 4px solid #e2e8f0;
  border-top-color: #1272c4;
  border-radius: 50%;
  animation: g3-spin 0.75s linear infinite;
}

@keyframes g3-spin {
  to { transform: rotate(360deg); }
}

.g3-logout-msg {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3748;
}

.g3-logout-fade-enter-active,
.g3-logout-fade-leave-active {
  transition: opacity 0.2s ease;
}
.g3-logout-fade-enter-from,
.g3-logout-fade-leave-to {
  opacity: 0;
}
</style>
